import { useEffect } from "react";
import "@/App.css";
import {
    BrowserRouter,
    Routes,
    Route,
    Navigate,
    useLocation,
} from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import useGlobalRevealObserver from "@/components/motion/useGlobalRevealObserver";
import useThirdPartyIframeTitler from "@/lib/useThirdPartyIframeTitler";
import { prefetchPromotedWinners } from "@/lib/experiments";

import Home from "@/pages/Home";
import HowItWorks from "@/pages/HowItWorks";
import Pricing from "@/pages/Pricing";
import { ForBuyers, ForInvestors } from "@/pages/Persona";
import SampleAnalysis from "@/pages/SampleAnalysis";
import Learn from "@/pages/Learn";
import LearnArticle from "@/pages/LearnArticle";
import Contact from "@/pages/Contact";
import LegalPage from "@/pages/Legal";
import AccountPrivacy from "@/pages/AccountPrivacy";
import Upload from "@/pages/Upload";
import AuthPage from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import AppleReturn from "@/pages/AppleReturn";
import UpgradePromptModal from "@/components/UpgradePromptModal";
import WelcomePanel from "@/components/WelcomePanel";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import RevertEmailChange from "@/pages/RevertEmailChange";
import RevokeDevice from "@/pages/RevokeDevice";
import RecoverAccount from "@/pages/RecoverAccount";
import Unsubscribe from "@/pages/Unsubscribe";
import SupportCenter from "@/pages/SupportCenter";
import Dashboard from "@/pages/Dashboard";
import Onboarding from "@/pages/Onboarding";

import ReportView from "@/pages/ReportView";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import Admin from "@/pages/Admin";
import AdminExperiments from "@/pages/admin/AdminExperiments";
import AdminExperimentDetail from "@/pages/admin/AdminExperimentDetail";
import AdminPartnerLeads from "@/pages/admin/AdminPartnerLeads";
import AdminContentPackages from "@/pages/admin/AdminContentPackages";
import AdminArticlePreview from "@/pages/admin/AdminArticlePreview";
import AdminWeeklyEmails from "@/pages/admin/AdminWeeklyEmails";
import AdminVisualAssetStorage from "@/pages/admin/AdminVisualAssetStorage";
import AdminMetricoolSettings from "@/pages/admin/AdminMetricoolSettings";
import AdminPinterestPerformance from "@/pages/admin/AdminPinterestPerformance";
import AdminAudienceInsights from "@/pages/admin/AdminAudienceInsights";
import AdminSubscriptionAnalytics from "@/pages/admin/AdminSubscriptionAnalytics";

import AdminNewsletterBacklog from "@/pages/admin/AdminNewsletterBacklog";
import AdminDistribution from "@/pages/admin/AdminDistribution";
import AdminCalculators from "@/pages/admin/AdminCalculators";
import AdminIssues from "@/pages/admin/AdminIssues";
import SharedReport from "@/pages/SharedReport";
import AccountSettings from "@/pages/AccountSettings";
import InvestorComparison from "@/pages/InvestorComparison";
import CookieConsent from "@/components/layout/CookieConsent";
import PreviewBadge from "@/components/layout/PreviewBadge";
import ToolsHub from "@/pages/tools/ToolsHub";
import ContingencyTimelinePlanner from "@/pages/tools/ContingencyTimelinePlanner";
import ClosingCostCalculator from "@/pages/tools/ClosingCostCalculator";
import CalculatorPage from "@/pages/tools/CalculatorPage";

// Handle scroll behavior on SPA route changes. Two cases:
//   1. `/path#anchor` — smooth-scroll to the anchor target once the page renders
//   2. `/path` (no hash) — reset to top so users don't land mid-page after
//      switching nav tabs.
function ScrollToHash() {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        if (hash) {
            // Wait one tick so the destination page has rendered the anchor target.
            const id = hash.replace("#", "");
            const tryScroll = (attempt = 0) => {
                const el = document.getElementById(id);
                if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                } else if (attempt < 10) {
                    setTimeout(() => tryScroll(attempt + 1), 80);
                }
            };
            tryScroll();
            return;
        }
        // No hash → reset scroll to top of the page.
        // `instant` avoids jarring smooth-scrolls when the previous page was deep.
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, [pathname, hash]);
    return null;
}

function ProtectedRoute({ children }) {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-bl-bg">
                <div className="bl-spinner" />
            </div>
        );
    }
    if (!user) {
        return (
            <Navigate
                to={"/auth/login?next=" + encodeURIComponent(window.location.pathname)}
                replace
            />
        );
    }
    return children;
}

function AppRouter() {
    const location = useLocation();
    useGlobalRevealObserver();
    useThirdPartyIframeTitler();
    useEffect(() => { prefetchPromotedWinners(); }, []);
    // SYNCHRONOUS check (during render): handle Emergent OAuth callback FIRST.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }

    return (
        <>
            <ScrollToHash />
            <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/for-buyers" element={<ForBuyers />} />
            <Route path="/for-investors" element={<ForInvestors />} />
            <Route path="/sample-analysis" element={<SampleAnalysis />} />
            {/* Legacy alias — older Learn articles + email templates link
                to /sample-report instead of /sample-analysis. Redirect so
                the public never lands on the homepage by mistake. */}
            <Route
                path="/sample-report"
                element={<Navigate to="/sample-analysis" replace />}
            />
            {/* Legacy legal-page aliases used by some CMS rich-text editor
                snippets. Redirect to the canonical legal routes. */}
            <Route
                path="/legal/privacy"
                element={<Navigate to="/privacy-policy" replace />}
            />
            <Route
                path="/legal/terms"
                element={<Navigate to="/terms" replace />}
            />
            <Route path="/learn" element={<Learn />} />
            <Route path="/learn/:slug" element={<LearnArticle />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/tools" element={<ToolsHub />} />
            {/* Legacy → engine slug redirects (v4.40.25 migration).
                Preserve old URLs for SEO; let the engine serve the page. */}
            <Route
                path="/tools/home-inspection-negotiation-calculator"
                element={<Navigate to="/tools/seller-credit-estimator" replace />}
            />
            <Route
                path="/tools/should-i-buy-this-house-inspection-quiz"
                element={<Navigate to="/tools/walk-away-from-house-calculator" replace />}
            />
            <Route
                path="/tools/roof-repair-cost-calculator"
                element={<Navigate to="/tools/roof-repair-cost-estimator" replace />}
            />
            <Route
                path="/tools/hvac-age-replacement-checker"
                element={<Navigate to="/tools/hvac-replacement-cost-calculator" replace />}
            />
            <Route
                path="/tools/electrical-panel-concern-checker"
                element={<Navigate to="/tools/electrical-panel-upgrade-cost-calculator" replace />}
            />
            {/* Legacy routes with no engine equivalent yet — keep serving the
                original component until engine versions are built. */}
            <Route
                path="/tools/inspection-contingency-timeline-planner"
                element={<ContingencyTimelinePlanner />}
            />
            <Route
                path="/tools/closing-cost-calculator"
                element={<ClosingCostCalculator />}
            />
            {/* Dynamic engine-driven calculators (v4.40.23+). Each slug
                resolves a calculator config from the backend. This catches
                all migrated slugs (foundation-crack-severity-checker,
                water-intrusion-risk-checker, seller-credit-estimator,
                repair-exposure-calculator) automatically. */}
            <Route
                path="/tools/:slug"
                element={<CalculatorPage />}
            />
            <Route path="/privacy-policy" element={<LegalPage pageKey="privacy" />} />
            <Route path="/terms" element={<LegalPage pageKey="terms" />} />
            <Route path="/cookie-policy" element={<LegalPage pageKey="cookie" />} />
            <Route path="/disclaimer" element={<LegalPage pageKey="disclaimer" />} />
            <Route path="/refund-policy" element={<LegalPage pageKey="refund" />} />
            <Route path="/accessibility" element={<LegalPage pageKey="accessibility" />} />
            <Route path="/ai-disclosure" element={<LegalPage pageKey="ai-disclosure" />} />
            <Route path="/affiliate" element={<LegalPage pageKey="affiliate" />} />
            <Route path="/professional-disclaimer" element={<LegalPage pageKey="professional-disclaimer" />} />
            <Route path="/security" element={<LegalPage pageKey="security" />} />
            <Route path="/trust" element={<LegalPage pageKey="trust" />} />
            <Route path="/account/privacy" element={<AccountPrivacy />} />

            <Route path="/upload" element={<Upload />} />

            <Route path="/auth/login" element={<AuthPage mode="login" />} />
            <Route path="/auth/register" element={<AuthPage mode="register" />} />
            <Route path="/auth/gate" element={<AuthPage mode="register" gate />} />
            <Route path="/apple-return" element={<AppleReturn />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/verify-email/:token" element={<VerifyEmail />} />
            <Route path="/revert-email-change/:token" element={<RevertEmailChange />} />
            <Route path="/security/revoke-device" element={<RevokeDevice />} />
            <Route path="/recover-account" element={<RecoverAccount />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/support" element={<SupportCenter />} />

            <Route
                path="/dashboard"
                element={
                    <ProtectedRoute>
                        <Dashboard />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/onboarding"
                element={
                    <ProtectedRoute>
                        <Onboarding />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/investor/comparison"
                element={
                    <ProtectedRoute>
                        <InvestorComparison />
                    </ProtectedRoute>
                }
            />
            <Route path="/report/:reportId" element={<ReportView />} />
            <Route path="/shared/:token" element={<SharedReport />} />
            <Route path="/checkout/success" element={<CheckoutSuccess />} />

            <Route
                path="/account"
                element={
                    <ProtectedRoute>
                        <AccountSettings />
                    </ProtectedRoute>
                }
            />
            <Route path="/settings" element={<Navigate to="/account" replace />} />

            <Route
                path="/admin"
                element={
                    <ProtectedRoute>
                        <Admin />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/experiments"
                element={
                    <ProtectedRoute>
                        <AdminExperiments />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/experiments/:id"
                element={
                    <ProtectedRoute>
                        <AdminExperimentDetail />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/partner-leads"
                element={
                    <ProtectedRoute>
                        <AdminPartnerLeads />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/audience-insights"
                element={
                    <ProtectedRoute>
                        <AdminAudienceInsights />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/subscription-analytics"
                element={
                    <ProtectedRoute>
                        <AdminSubscriptionAnalytics />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/subscription-economics"
                element={
                    <ProtectedRoute>
                        <AdminSubscriptionAnalytics />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/content"
                element={
                    <ProtectedRoute>
                        <AdminContentPackages />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/content/:packageId/article-preview"
                element={
                    <ProtectedRoute>
                        <AdminArticlePreview />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/weekly-emails"
                element={
                    <ProtectedRoute>
                        <AdminWeeklyEmails />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/visual-asset-storage"
                element={
                    <ProtectedRoute>
                        <AdminVisualAssetStorage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/metricool"
                element={
                    <ProtectedRoute>
                        <AdminMetricoolSettings />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/metricool/performance"
                element={
                    <ProtectedRoute>
                        <AdminPinterestPerformance />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/distribution"
                element={
                    <ProtectedRoute>
                        <AdminDistribution />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/newsletter"
                element={
                    <ProtectedRoute>
                        <AdminNewsletterBacklog />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/calculators"
                element={
                    <ProtectedRoute>
                        <AdminCalculators />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/issues"
                element={
                    <ProtectedRoute>
                        <AdminIssues />
                    </ProtectedRoute>
                }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </>
    );
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <AppRouter />
                    <CookieConsent />
                    <PreviewBadge />
                    <UpgradePromptModal />
                    <WelcomePanel />
                    <Toaster position="bottom-right" />
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
